import { useHotkeys } from 'react-hotkeys-hook';
import { ui$ } from '../store/ui$';
import { isImeComposing } from '../utils/imeUtils';

// Local overlays do not all have a ui$.activeModal entry. Their DOM presence
// also claims keyboard ownership, including when a button has focus.
const BLOCKING_LAYER_SELECTOR = [
  '.highlight-popover',
  '.focus-selection-popover',
  '.slide-panel-backdrop',
  '.image-lightbox-overlay',
  '[role="dialog"][aria-modal="true"]',
  '[role="menu"]',
  '[role="listbox"]',
].join(', ');

const isActiveShortcutLayer = (element) => {
  // Embedded widgets can keep modal markup mounted while the widget is closed.
  // Hidden/inert ancestors also remove a layer from keyboard ownership.
  if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  for (let node = element; node; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
  }
  return true;
};

export const hasBlockingShortcutLayer = () => (
  ui$.searchModalOpen.get() ||
  ui$.shortcutsModalOpen.get() ||
  Boolean(ui$.activeModal.get()) ||
  Array.from(document.querySelectorAll(BLOCKING_LAYER_SELECTOR)).some(isActiveShortcutLayer)
);

/** App actions yield to composition and the foreground layer before consuming a key. */
export const useAppHotkeys = (keys, callback, options = {}) => {
  const { allowWhenBlocked = false, ignoreEventWhen, ...hotkeyOptions } = options;
  return useHotkeys(keys, callback, {
    ...hotkeyOptions,
    ignoreEventWhen: event => (
      event.defaultPrevented ||
      isImeComposing(event) ||
      ui$.isComposing.get() ||
      (!(typeof allowWhenBlocked === 'function' ? allowWhenBlocked() : allowWhenBlocked) && hasBlockingShortcutLayer()) ||
      Boolean(ignoreEventWhen?.(event))
    ),
  });
};
